import { useMemo } from 'react';
import { FAST_USER_INTERACTION_DEBOUNCE } from '../../../../constants';
import { useDebouncedValue } from '../../../../hooks/useDebounceValue';
import { useHannibalQuery } from '../../../../schema/hannibal/HannibalContext';
import {
  PracticeSuggestionsDocument,
  SuggestionPracticeFragment,
  SuggestionPractitionerFragment,
} from '../../../../schema/hannibal/types.generated';
import { EMPTY_LIST } from '../../../../util/EMPTY_LIST';
import { SuggestionList } from '../SuggestionList/SuggestionList';
import { TabProps } from '../TabProps';
import { SuggestionData } from '../types';
import { useTextSuggestion } from '../useTextSuggestion';

export const mapPracticeSuggestionsToSuggestionData = (
  profiles: readonly SuggestionPracticeFragment[],
): SuggestionData[] =>
  profiles
    .filter((it): it is SuggestionPracticeFragment => it.__typename === 'Practice')
    .map(
      (it): SuggestionData => ({
        type: 'PracticeSuggestion',
        id: `practice-${it.id}`,
        practice: {
          id: it.id,
          name: it.name,
          specialties: it.specialties?.map(it => it.name) ?? [],
          suburb: it.address?.suburb ?? '',
          state: it.address?.state ?? '',
          url: it.url,
        },
      }),
    );

// experimental suggestions
export const mapProfileSuggestionsToSuggestionData = (
  profiles: readonly (SuggestionPracticeFragment | SuggestionPractitionerFragment)[],
): SuggestionData[] =>
  profiles.map(
    (it): SuggestionData => {
      if (it.__typename === 'Practice') {
        return {
          type: 'PracticeSuggestion',
          id: `practice-${it.id}`,
          practice: {
            id: it.id,
            name: it.name,
            specialties: it.specialties?.map(it => it.name) ?? [],
            suburb: it.address?.suburb ?? '',
            state: it.address?.state ?? '',
            url: it.url,
          },
        };
      } else {
        return {
          type: 'PractitionerSuggestion',
          id: `practitioner-${it.id}`,
          practitioner: {
            id: it.id,
            title: it.title ?? undefined,
            firstName: it.firstName ?? undefined,
            lastName: it.lastName ?? undefined,
            specialties: it.specialties?.map(specialty => specialty.name) ?? [],
            suburb: it.primaryPractice?.address?.suburb ?? undefined,
            state: it.primaryPractice?.address?.state ?? undefined,
            imagePath: it.profilePhoto?.link ?? undefined,
            url: it.url,
          },
        };
      }
    },
  );

export const PracticeSuggestions = ({ searchTerm, onChoose, trackingData }: TabProps) => {
  const query = useDebouncedValue(searchTerm, FAST_USER_INTERACTION_DEBOUNCE);
  const { data } = useHannibalQuery(PracticeSuggestionsDocument, { variables: { query } });

  const profiles = data?.searchText.profiles ?? EMPTY_LIST;

  const practiceSuggestions: SuggestionData[] = useMemo(
    () => mapPracticeSuggestionsToSuggestionData(profiles),
    [profiles],
  );

  const suggestions: SuggestionData[] = useTextSuggestion(searchTerm, practiceSuggestions);

  return (
    <SuggestionList
      suggestions={suggestions}
      onChoose={onChoose}
      trackingData={trackingData}
      searchTerm={searchTerm}
    />
  );
};
